import {Amplify} from 'aws-amplify';
import {generateClient, GraphQLResult} from 'aws-amplify/api';
import type {UploadFile} from 'antd/lib/upload/interface';
import {
  ClassSchool,
  CreateClassSchoolInput,
  CreateSchoolInput,
  CreateTeacherInput,
  CreateUserInput,
  InitEnvironmentVariable,
  InitWithAmplify,
  PaginatedSchoolsResponse,
  SchoolProfile,
  TeacherProfile,
  UpdateClassSchoolInput,
  UpdateSchoolInput,
  User,
  UserService as PikadoUserService,
} from '@PikaDoApp/npm-pikado-logic';

import {ISchoolService} from './ISchoolService';
import {
  AssetInsert,
  ClassesConnection,
  SchoolsConnection,
} from '../../graphql/API';
import {getEnvironment} from '../../lib/getEnvironment';
import {StorageService, UploadFileType} from '../Storage/StorageService';

InitWithAmplify(Amplify);
InitEnvironmentVariable(getEnvironment());

const client = generateClient();

const subscribeClassWithDiscountId = /* GraphQL */ `
  mutation SubscribeClassWithDiscountId(
    $classschoolId: String!
    $discountcodeId: String!
    $packageId: String!
  ) {
    subscribeClassWithDiscountId(
      classschoolId: $classschoolId
      discountcodeId: $discountcodeId
      packageId: $packageId
    )
  }
`;

const classSchoolWithStudentsEnrollments = /* GraphQL */ `
  query Classschool($id: String!) {
    classschool(id: $id) {
      id
      createdAt
      updatedAt
      name
      schoolYear
      students {
        id
        createdAt
        updatedAt
        name
        age
        birthDate
        gender
        enrollments {
          id
          createdAt
          updatedAt
          paymentStatus
          curriculum {
            id
            name
          }
          level {
            id
            name
          }
          grade {
            id
            name
          }
          package {
            id
            packageDuration
          }
        }
      }
    }
  }
`;

const importCsvMutation = /* GraphQL */ `
  mutation ImportCsv($s3Url: String!) {
    importCsv(s3Url: $s3Url)
  }
`;

const getImportStatusQuery = /* GraphQL */ `
  query GetImportStatus($importId: String!) {
    getImportStatus(importId: $importId) {
      importId
      s3Url
      status
      totalSchools
      doneSchools
      failedSchools
      failedAttempts
      createdAt
      updatedAt
      validationErrors {
        sheet
        row
        messages
      }
      schools {
        tempId
        name
        email
        status
        error
        schoolId
        userId
        teacherCount
        teachersDone
        teachersFailed
        classCount
        classesDone
        classesFailed
        studentCount
        studentsDone
        studentsFailed
        retryCount
        failedAttempts
        maxAttempts
      }
    }
  }
`;

type SchoolWithId = SchoolProfile & {id: string};
type ClassSchoolWithId = ClassSchool & {id: string};
export type SchoolImportValidationError = {
  sheet: string;
  row: number;
  messages: string[];
};

export type SchoolImportStatusSchoolItem = {
  tempId: string;
  name: string;
  email: string;
  status: string;
  error?: string;
  schoolId?: string;
  userId?: string;
  teacherCount: number;
  teachersDone: number;
  teachersFailed: number;
  classCount: number;
  classesDone: number;
  classesFailed: number;
  studentCount: number;
  studentsDone: number;
  studentsFailed: number;
  retryCount: number;
  failedAttempts: number;
  maxAttempts: number;
};

export type SchoolImportStatus = {
  importId: string;
  s3Url: string;
  status: string;
  totalSchools: number;
  doneSchools: number;
  failedSchools: number;
  failedAttempts: number;
  createdAt: string;
  updatedAt: string;
  validationErrors: SchoolImportValidationError[];
  schools: SchoolImportStatusSchoolItem[];
};

const normalizeSchool = (school: SchoolProfile): SchoolWithId => ({
  ...school,
  id: (school as any).id ?? school?._id ?? school?.oldId ?? '',
});

const normalizeClassSchool = (classSchool: ClassSchool): ClassSchoolWithId => ({
  ...classSchool,
  id: (classSchool as any).id ?? classSchool?._id ?? classSchool?.oldId ?? '',
});

export class SchoolService implements ISchoolService {
  private pikadoUserService: PikadoUserService;
  private storageService: StorageService;

  constructor() {
    this.pikadoUserService = new PikadoUserService();
    this.storageService = new StorageService();
  }

  async createUser(input: CreateUserInput): Promise<User> {
    return await this.pikadoUserService.createUser(input);
  }

  async getUserByEmail(email: string): Promise<User> {
    return await this.pikadoUserService.getUserByEmail(email);
  }

  async getAllSchools(
    limit?: number,
    offset?: number,
    search?: string,
  ): Promise<PaginatedSchoolsResponse> {
    InitWithAmplify(Amplify);
    InitEnvironmentVariable(getEnvironment());
    const response = await this.pikadoUserService.getAllSchools(
      limit,
      offset,
      search,
    );

    return {
      ...response,
      items: (response?.items ?? []).map(normalizeSchool),
    };
  }

  async getSchoolByUserId(userId: string): Promise<SchoolProfile> {
    return normalizeSchool(
      await this.pikadoUserService.getSchoolByUserId(userId),
    );
  }

  async getSchoolById(id: string): Promise<SchoolProfile> {
    return normalizeSchool(await this.pikadoUserService.getSchoolById(id));
  }

  async createSchool(
    input: CreateSchoolInput,
    promoInsert?: AssetInsert,
  ): Promise<SchoolProfile> {
    const payload = {...input};

    if (promoInsert) {
      payload.logoUrl = promoInsert.fileUri || promoInsert.thumbnailUri;
    }

    return normalizeSchool(await this.pikadoUserService.createSchool(payload));
  }

  async updateSchool(
    input: UpdateSchoolInput,
    promoInsert?: AssetInsert,
  ): Promise<SchoolProfile> {
    const payload = {...input};

    if (promoInsert) {
      payload.logoUrl = promoInsert.fileUri || promoInsert.thumbnailUri;
    }

    return normalizeSchool(await this.pikadoUserService.updateSchool(payload));
  }

  async deleteSchool(id: string): Promise<SchoolProfile> {
    return normalizeSchool(await this.pikadoUserService.deleteSchool(id));
  }

  async getClassSchoolById(id: string): Promise<ClassSchool | any> {
    const classSchool = await this.pikadoUserService.getClassSchoolById(id);
    return normalizeClassSchool(classSchool);
  }

  async getClassSchoolsBySchoolId(schoolId: string): Promise<ClassSchool[]> {
    const classes =
      await this.pikadoUserService.getClassSchoolsBySchoolId(schoolId);
    return (classes ?? []).map(normalizeClassSchool);
  }

  async createClassSchool(
    inputOrSchoolId: CreateClassSchoolInput | string,
    _teacherId?: string,
    name?: string,
    _schoolYear?: string,
    levelId?: string,
  ): Promise<ClassSchool> {
    const input =
      typeof inputOrSchoolId === 'string'
        ? {
            schoolId: inputOrSchoolId,
            name: name ?? 'Class',
            levelId,
          }
        : inputOrSchoolId;

    const classSchool = await this.pikadoUserService.createClassSchool(input);
    return normalizeClassSchool(classSchool);
  }

  async updateClassSchool(input: UpdateClassSchoolInput): Promise<ClassSchool> {
    const classSchool = await this.pikadoUserService.updateClassSchool(input);
    return normalizeClassSchool(classSchool);
  }

  async deleteClassSchool(id: string): Promise<ClassSchool> {
    const classSchool = await this.pikadoUserService.deleteClassSchool(id);
    return normalizeClassSchool(classSchool);
  }

  async createTeacher(
    inputOrUserId: CreateTeacherInput | string,
    _email?: string,
    name?: string,
    _phoneNumber?: string,
    schoolId?: string,
  ): Promise<TeacherProfile> {
    const input =
      typeof inputOrUserId === 'string'
        ? {
            userId: inputOrUserId,
            name: name ?? 'Teacher',
            schoolId,
          }
        : inputOrUserId;

    return await this.pikadoUserService.createTeacher(input);
  }

  async getSchools(
    nextToken: string,
    searchValue?: string,
  ): Promise<SchoolsConnection> {
    const limit = 100;
    const offset = nextToken ? Number(nextToken) : 0;
    const response = await this.getAllSchools(limit, offset, searchValue);
    const items = (response?.items ?? []) as any[];

    return {
      items: items as any,
      nextToken:
        offset + items.length < (response?.totalCount ?? 0)
          ? String(offset + items.length)
          : null,
      scannedCount: items.length,
      __typename: 'SchoolsConnection',
    };
  }

  async getClassesBySchoolId(schoolId: string): Promise<ClassesConnection> {
    const items = await this.getClassSchoolsBySchoolId(schoolId);

    return {
      items: items as any,
      nextToken: null,
      scannedCount: items.length,
      __typename: 'ClassesConnection',
    };
  }

  async activateSubsctiptionInClass(
    classschoolId: string,
    discountcodeId: string,
    packageId: string,
  ): Promise<string> {
    const response: GraphQLResult<any> = await client.graphql({
      query: subscribeClassWithDiscountId,
      variables: {
        classschoolId,
        discountcodeId,
        packageId,
      },
    });

    return response.data?.subscribeClassWithDiscountId;
  }

  async getClassSchoolWithStudentsEnrollments(
    classschoolId: string,
  ): Promise<any> {
    const response: GraphQLResult<any> = await client.graphql({
      query: classSchoolWithStudentsEnrollments,
      variables: {
        id: classschoolId,
      },
    });

    return response.data?.classschool;
  }

  async uploadImportFile(file: UploadFile): Promise<string> {
    return await this.storageService.uploadFile(
      file,
      UploadFileType.SCHOOL_IMPORT_XLSX,
    );
  }

  async importCsv(s3Url: string): Promise<string> {
    return this.pikadoUserService.importCsv(s3Url);
  }

  async getImportStatus(importId: string): Promise<SchoolImportStatus> {
    return this.pikadoUserService.getImportStatus(importId);
  }
}
